import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { scooterApi } from "@/lib/api";
import { toast } from "sonner";
import { MechanicalChecklistForm } from "@/components/MechanicalChecklist";
import { 
  Save, 
  ArrowLeft,
  Info,
  Camera,
  Settings,
  Wrench,
  History
} from "lucide-react";

export default function AddScooterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    engine_cc: 50,
    color: "",
    vin: "",
    mileage: 0,
    purchase_price: 0,
    selling_price: 0,
    min_selling_price: 0,
    status: "reception",
    scooter_type: "standard",
    damage_history: "fara_accidente",
    reception_data: {
      scooter_code: "",
      repair_priority: 2,
      reception_condition: 3,
      reception_notes: "",
      container_reference: "",
      supplier: ""
    },
    technical_file: {
      engine_type: "",
      transmission: "",
      cooling_system: "",
      fuel_type: "",
      max_speed: "",
      consumption: "",
    },
    mechanical_check: {
      // Service Info
      service_info: {
        dernier_vidange_effectue: false,
        vidange_effectue_par: "inconnu",
        date_derniere_vidange: "",
        km_derniere_vidange: 0,
        type_interventions: "periodiques"
      },
      // Motor & Transmisie
      motor_stare_generala: 3,
      pornire_motor: 3,
      compresie_motor: 3,
      zgomote_anormale: 3,
      pierderi_ulei: 3,
      ambreiaj: 3,
      curea_transmisie: 3,
      variator: 3,
      role_variator: 3,
      cutie_transmisie: 3,
      // Lubrifiere
      nivel_ulei_motor: 3,
      calitate_ulei: 3,
      filtru_ulei: 3,
      filtru_aer: 3,
      bujie: 3,
      // Franare
      frana_fata: 3,
      frana_spate: 3,
      placute_frana: 3,
      discuri_frana: 3,
      conducte_frana: 3,
      lichid_frana: 3,
      // Roti & Suspensii
      anvelopa_fata: 3,
      anvelopa_spate: 3,
      presiune_anvelope: 3,
      jante: 3,
      rulmenti_roti: 3,
      furca_fata: 3,
      amortizor_spate: 3,
      // Sistem Electric
      baterie: 3,
      incarcare_baterie: 3,
      alternator: 3,
      demaror: 3,
      instalatie_electrica: 3,
      faruri: 3,
      stop: 3,
      semnalizari: 3,
      claxon: 3,
      bord: 3,
      // Cadru
      cadru: 3,
      suduri: 3,
      suport_motor: 3,
      cric: 3,
      // Evacuare
      toba_evacuare: 3,
      garnituri_evacuare: 3,
      zgomot_evacuare: 3,
      // Siguranta
      ghidon: 3,
      acceleratie: 3,
      manete_frana: 3,
      oglinzi: 3,
      blocare_directie: 3,
      // Aspect
      carene: 3,
      sa: 3,
      portbagaj: 3,
      stare_generala: 3,
      // Concluzie
      scor_general: 3,
      recomandari_mecanic: "",
      verification_status: "neverificat",
      aprobat_utilizare: true,
      // Acte
      acte_disponibile: true,
      tip_acte: "carte_rar",
      tara_documente: "",
      observatii_acte: "",
      // Observatii
      observations: "",
    },
    repairs_done: "",
    costs: 0,
    warranty_info: "",
    internal_notes: "",
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTechnicalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      technical_file: { ...prev.technical_file, [field]: value }
    }));
  };

  const handleMechanicalCheckChange = (newValues) => {
    setFormData(prev => ({
      ...prev,
      mechanical_check: newValues
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model || !formData.vin) {
      toast.error("Please fill in required fields: Brand, Model, VIN");
      return;
    }

    setLoading(true);
    try {
      const scooter = await scooterApi.create(formData);
      toast.success(`Scooter ${scooter.scooter_id} created successfully!`);
      navigate(`/stock/${scooter.scooter_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create scooter");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-scooter-form">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading font-black text-2xl md:text-3xl">
                Add New Scooter
              </h1>
              <p className="text-muted-foreground text-sm">
                Adaugă scooter nou în stoc
              </p>
            </div>
          </div>
          <Button 
            type="submit" 
            className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
            disabled={loading}
            data-testid="btn-save-scooter"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General Information */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-primary" />
                General Information / Informații generale
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand / Marcă *</Label>
                  <Input
                    id="brand"
                    placeholder="e.g., Vespa, Piaggio"
                    value={formData.brand}
                    onChange={(e) => handleChange("brand", e.target.value)}
                    className="font-mono"
                    data-testid="input-brand"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model *</Label>
                  <Input
                    id="model"
                    placeholder="e.g., Primavera 125"
                    value={formData.model}
                    onChange={(e) => handleChange("model", e.target.value)}
                    className="font-mono"
                    data-testid="input-model"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="year">Year / An fabricație</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1990"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={(e) => handleChange("year", parseInt(e.target.value))}
                    className="font-mono"
                    data-testid="input-year"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="engine_cc">Engine (cc) / Capacitate</Label>
                  <Input
                    id="engine_cc"
                    type="number"
                    min="25"
                    max="1000"
                    value={formData.engine_cc}
                    onChange={(e) => handleChange("engine_cc", parseInt(e.target.value))}
                    className="font-mono"
                    data-testid="input-engine-cc"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="color">Color / Culoare</Label>
                  <Input
                    id="color"
                    placeholder="e.g., Red, Blue"
                    value={formData.color}
                    onChange={(e) => handleChange("color", e.target.value)}
                    className="font-mono"
                    data-testid="input-color"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mileage">Mileage (km) / Kilometri</Label>
                  <Input
                    id="mileage"
                    type="number"
                    min="0"
                    value={formData.mileage}
                    onChange={(e) => handleChange("mileage", parseInt(e.target.value))}
                    className="font-mono"
                    data-testid="input-mileage"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vin">VIN / Chassis Number / Număr șasiu *</Label>
                <Input
                  id="vin"
                  placeholder="Enter VIN number"
                  value={formData.vin}
                  onChange={(e) => handleChange("vin", e.target.value.toUpperCase())}
                  className="font-mono uppercase"
                  data-testid="input-vin"
                />
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_price">Purchase Price / Pret achat (EUR)</Label>
                  <Input
                    id="purchase_price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.purchase_price || ""}
                    onChange={(e) => handleChange("purchase_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="font-mono"
                    data-testid="input-purchase-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="selling_price">Pret vanzare (EUR)</Label>
                  <Input
                    id="selling_price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.selling_price || ""}
                    onChange={(e) => handleChange("selling_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="font-mono"
                    data-testid="input-selling-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min_selling_price">Pret minim vanzare (EUR)</Label>
                  <Input
                    id="min_selling_price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.min_selling_price || ""}
                    onChange={(e) => handleChange("min_selling_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="font-mono"
                    data-testid="input-min-selling-price"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reception">Reception</SelectItem>
                    <SelectItem value="preparation">Preparation</SelectItem>
                    <SelectItem value="in_stock">In Stock / In stoc</SelectItem>
                    <SelectItem value="reserved">Reserved / Rezervat</SelectItem>
                    <SelectItem value="in_service">In Service / In service</SelectItem>
                    <SelectItem value="sold">Sold / Vandut</SelectItem>
                    <SelectItem value="casse">Casse / Dezmembrare</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* New fields: Scooter Type and Damage History */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scooter_type">Tip Scuter</Label>
                  <Select
                    value={formData.scooter_type}
                    onValueChange={(value) => handleChange("scooter_type", value)}
                  >
                    <SelectTrigger data-testid="select-scooter-type">
                      <SelectValue placeholder="Selectează tip" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fara_permis">🛵 Fără permis (25 km/h)</SelectItem>
                      <SelectItem value="standard">🏍️ Standard (50–125 cc)</SelectItem>
                      <SelectItem value="maxi">🔥 Maxi-scooter (250–750 cc)</SelectItem>
                      <SelectItem value="electric">⚡ Electric</SelectItem>
                      <SelectItem value="benzina">⛽ Benzină</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="damage_history">Istoric Daune</Label>
                  <Select
                    value={formData.damage_history}
                    onValueChange={(value) => handleChange("damage_history", value)}
                  >
                    <SelectTrigger data-testid="select-damage-history">
                      <SelectValue placeholder="Selecteaza istoric" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fara_accidente">Fara accidente</SelectItem>
                      <SelectItem value="lovit_usor">Lovit usor (estetic)</SelectItem>
                      <SelectItem value="lovit_serios">Lovit serios (structural)</SelectItem>
                      <SelectItem value="reconditionat">Reconditionat complet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Reception Data Section - Show only for reception/preparation status */}
              {(formData.status === "reception" || formData.status === "preparation") && (
                <>
                  <Separator />
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <h3 className="font-bold text-purple-400 mb-4 flex items-center gap-2">
                      📦 Informations de Reception
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="scooter_code">Code Scooter (pour ecrire sur le scooter)</Label>
                        <Input
                          id="scooter_code"
                          placeholder="Ex: A01, B12..."
                          value={formData.reception_data.scooter_code}
                          onChange={(e) => setFormData({
                            ...formData,
                            reception_data: { ...formData.reception_data, scooter_code: e.target.value }
                          })}
                          className="font-mono"
                          data-testid="input-scooter-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="repair_priority">Priorite de Reparation</Label>
                        <Select
                          value={String(formData.reception_data.repair_priority)}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            reception_data: { ...formData.reception_data, repair_priority: parseInt(value) }
                          })}
                        >
                          <SelectTrigger data-testid="select-repair-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">🔴 Urgent</SelectItem>
                            <SelectItem value="2">🟡 Normal</SelectItem>
                            <SelectItem value="3">🔵 Peut attendre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reception_condition">Etat a la Reception (1-5)</Label>
                        <Select
                          value={String(formData.reception_data.reception_condition)}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            reception_data: { ...formData.reception_data, reception_condition: parseInt(value) }
                          })}
                        >
                          <SelectTrigger data-testid="select-reception-condition">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 - Tres mauvais</SelectItem>
                            <SelectItem value="2">2 - Mauvais</SelectItem>
                            <SelectItem value="3">3 - Moyen</SelectItem>
                            <SelectItem value="4">4 - Bon</SelectItem>
                            <SelectItem value="5">5 - Excellent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="container_reference">Reference Conteneur</Label>
                        <Input
                          id="container_reference"
                          placeholder="Ex: CONT-2024-001"
                          value={formData.reception_data.container_reference}
                          onChange={(e) => setFormData({
                            ...formData,
                            reception_data: { ...formData.reception_data, container_reference: e.target.value }
                          })}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="reception_notes">Remarques de Reception</Label>
                      <Textarea
                        id="reception_notes"
                        placeholder="Etat general, reparations a prevoir, remarques..."
                        value={formData.reception_data.reception_notes}
                        onChange={(e) => setFormData({
                          ...formData,
                          reception_data: { ...formData.reception_data, reception_notes: e.target.value }
                        })}
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Technical File */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5 text-primary" />
                Technical File / Fișă tehnică
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="engine_type">Engine Type / Tip motor</Label>
                  <Input
                    id="engine_type"
                    placeholder="e.g., 4-stroke"
                    value={formData.technical_file.engine_type}
                    onChange={(e) => handleTechnicalChange("engine_type", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transmission">Transmission / Transmisie</Label>
                  <Input
                    id="transmission"
                    placeholder="e.g., CVT automatic"
                    value={formData.technical_file.transmission}
                    onChange={(e) => handleTechnicalChange("transmission", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cooling_system">Cooling System / Răcire</Label>
                  <Input
                    id="cooling_system"
                    placeholder="e.g., Air-cooled"
                    value={formData.technical_file.cooling_system}
                    onChange={(e) => handleTechnicalChange("cooling_system", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fuel_type">Fuel Type / Combustibil</Label>
                  <Input
                    id="fuel_type"
                    placeholder="e.g., Petrol"
                    value={formData.technical_file.fuel_type}
                    onChange={(e) => handleTechnicalChange("fuel_type", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_speed">Max Speed / Viteză maximă</Label>
                  <Input
                    id="max_speed"
                    placeholder="e.g., 90 km/h"
                    value={formData.technical_file.max_speed}
                    onChange={(e) => handleTechnicalChange("max_speed", e.target.value)}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="consumption">Consumption / Consum</Label>
                  <Input
                    id="consumption"
                    placeholder="e.g., 2.5L/100km"
                    value={formData.technical_file.consumption}
                    onChange={(e) => handleTechnicalChange("consumption", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              {/* Tip Acte */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Tip Acte / Documents</Label>
                  <Select value={formData.technical_file.tip_acte} onValueChange={(v) => handleTechnicalChange("tip_acte", v)}>
                    <SelectTrigger><SelectValue placeholder="Selecteaza..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="carte_rar">Acte Romania</SelectItem>
                      <SelectItem value="carte_strainatate">Acte strainatate</SelectItem>
                      <SelectItem value="fara_acte">Doar factura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Observații acte</Label>
                  <Input
                    placeholder="Detalii despre acte..."
                    value={formData.technical_file.observatii_acte}
                    onChange={(e) => handleTechnicalChange("observatii_acte", e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mechanical Checklist */}
          <Card className="border-border lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wrench className="w-5 h-5 text-primary" />
                Fiche de Contrôle Mécanique / Mechanical Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MechanicalChecklistForm
                values={formData.mechanical_check}
                onChange={handleMechanicalCheckChange}
              />
            </CardContent>
          </Card>

          {/* History & Notes */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-primary" />
                History & Notes / Istoric și note
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="repairs_done">Repairs Done / Reparații efectuate</Label>
                <Textarea
                  id="repairs_done"
                  placeholder="List any repairs performed..."
                  value={formData.repairs_done}
                  onChange={(e) => handleChange("repairs_done", e.target.value)}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="costs">Total Costs / Costuri (€)</Label>
                  <Input
                    id="costs"
                    type="number"
                    min="0"
                    step="10"
                    value={formData.costs}
                    onChange={(e) => handleChange("costs", parseFloat(e.target.value))}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="warranty_info">Warranty / Garanție</Label>
                  <Input
                    id="warranty_info"
                    placeholder="e.g., 6 months"
                    value={formData.warranty_info}
                    onChange={(e) => handleChange("warranty_info", e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="internal_notes">Internal Notes / Note interne</Label>
                <Textarea
                  id="internal_notes"
                  placeholder="Private notes for staff..."
                  value={formData.internal_notes}
                  onChange={(e) => handleChange("internal_notes", e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info about photos */}
        <Card className="border-border border-dashed">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Photos & Documents</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  After saving, you'll be able to upload photos (Front, Rear, Left, Right, Engine, Dashboard) 
                  and documents (Registration, Invoice, etc.) on the scooter detail page.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  După salvare, veți putea încărca fotografii și documente pe pagina de detalii.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button (Mobile) */}
        <div className="lg:hidden">
          <Button 
            type="submit" 
            className="w-full bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
            disabled={loading}
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving..." : "Save Scooter"}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
